/**
 * Analytics Panel Component
 * Displays comprehensive analytics with revenue trends, forecasts, and insights
 */

import React, { useState, useEffect } from 'react';
import {
  BarChart3, Users, TrendingUp, TrendingDown, MapPin, FileText,
  DollarSign, Activity, Droplets, Calendar, Target, Award, Zap
} from 'lucide-react';
import apiClient from '../services/apiClient';

// Helper function for authenticated fetch requests
async function authFetch(url) {
  const headers = {};
  try {
    const token = await apiClient.getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  } catch (e) { /* proceed without auth */ }
  return fetch(url, { headers });
}

// Simple sparkline component
const Sparkline = ({ data, width = 120, height = 30, color = '#10b981' }) => {
  if (!data || data.length === 0) return null;

  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;

  const points = data.map((value, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((value - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} className="sparkline">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        points={points}
      />
    </svg>
  );
};

// Format large numbers
const formatNumber = (num, decimals = 1) => {
  if (num >= 1000000000) return `${(num / 1000000000).toFixed(decimals)}B`;
  if (num >= 1000000) return `${(num / 1000000).toFixed(decimals)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(decimals)}K`;
  return num?.toFixed?.(decimals) || num;
};

// MoM Change indicator component
const ChangeIndicator = ({ change, size = 'normal' }) => {
  if (change === null || change === undefined) return null;
  const numChange = parseFloat(change);
  const isPositive = numChange >= 0;
  const Icon = isPositive ? TrendingUp : TrendingDown;
  const className = `change-indicator ${isPositive ? 'positive' : 'negative'} ${size}`;

  return (
    <span className={className}>
      <Icon size={size === 'small' ? 12 : 16} />
      {Math.abs(numChange).toFixed(1)}%
    </span>
  );
};

function AnalyticsPanel({ analyticsData, onFilterClick }) {
  const [revenueData, setRevenueData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [topDsus, setTopDsus] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [ownershipData, setOwnershipData] = useState(null);
  const [loading, setLoading] = useState(false);

  console.log('AnalyticsPanel render. analyticsData:', analyticsData);

  // Handle row click to trigger filter
  const handleRowClick = (filterType, value) => {
    if (onFilterClick) {
      console.log('📊 Analytics filter clicked:', filterType, value);
      onFilterClick(filterType, value);
    }
  };

  // Extract counties from GIS query results for context-aware filtering
  const getCountiesFromResults = () => {
    if (!analyticsData?.summary?.byField?.county) return null;
    const counties = Object.keys(analyticsData.summary.byField.county);
    return counties.length > 0 ? counties : null;
  };

  // Fetch all enhanced data when panel is shown
  useEffect(() => {
    async function fetchAllData() {
      setLoading(true);
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL ?? 'http://localhost:5001';
        const counties = getCountiesFromResults();
        const countyParam = counties ? `?counties=${encodeURIComponent(counties.join(','))}` : '';

        console.log('📊 Fetching enhanced analytics data with filter:', counties);

        // Build query params for top-dsus (handle empty countyParam)
        const topDsusParams = counties
          ? `?counties=${encodeURIComponent(counties.join(','))}&limit=5`
          : '?limit=5';

        // Fetch all data in parallel
        const [revenueRes, trendRes, topDsusRes, forecastRes, ownershipRes] = await Promise.all([
          authFetch(`${backendUrl}/api/data/revenue/totals${countyParam}`),
          authFetch(`${backendUrl}/api/data/revenue/trend${countyParam}`),
          authFetch(`${backendUrl}/api/data/revenue/top-dsus${topDsusParams}`),
          authFetch(`${backendUrl}/api/data/forecast/summary`),
          authFetch(`${backendUrl}/api/data/ownership/by-tracts`)
        ]);

        if (revenueRes.ok) setRevenueData(await revenueRes.json());
        if (trendRes.ok) setTrendData(await trendRes.json());
        if (topDsusRes.ok) setTopDsus(await topDsusRes.json());
        if (forecastRes.ok) setForecastData(await forecastRes.json());
        if (ownershipRes.ok) setOwnershipData(await ownershipRes.json());

      } catch (error) {
        console.log('Enhanced analytics data not available:', error.message);
      }
      setLoading(false);
    }

    if (analyticsData) {
      fetchAllData();
    }
  }, [analyticsData]);

  if (!analyticsData) {
    console.log('AnalyticsPanel: No data, returning null');
    return null;
  }

  const { summary, query } = analyticsData;
  console.log('AnalyticsPanel: Rendering with query:', query, 'summary:', summary);

  // Parse the summary data
  const totals = {
    count: summary.totalCount || 0,
    acreage: summary.totalAcreage || 0,
    royaltyAcres: summary.totalRoyaltyAcres || 0,
    nri: summary.totalNRI || 0,
    orri: summary.totalORRI || 0
  };

  // Detect data type (tracts vs wells)
  const dataType = summary.dataType || 'tracts';

  // Convert grouped data to arrays for tables
  const operatorData = summary.byField?.operator
    ? Object.entries(summary.byField.operator).map(([name, count]) => ({ name, count }))
    : [];

  const countyData = summary.byField?.county
    ? Object.entries(summary.byField.county).map(([name, count]) => ({ name, count }))
    : [];

  const statusData = summary.byField?.leaseStatus
    ? Object.entries(summary.byField.leaseStatus).map(([name, count]) => ({ name, count }))
    : [];

  const formationData = summary.byField?.formation
    ? Object.entries(summary.byField.formation).map(([name, count]) => ({ name, count }))
    : [];

  // Get sparkline data from trend
  const sparklineData = trendData?.trend?.map(t => t.revenue) || [];

  // Calculate current MoM change
  const currentMoM = trendData?.trend?.length > 1
    ? trendData.trend[trendData.trend.length - 2]?.change
    : null;

  return (
    <div className="analytics-panel">
      <div className="analytics-header">
        <BarChart3 size={20} />
        <h3>Analytics Results</h3>
        <span className="analytics-query">{query}</span>
      </div>

      <div className="analytics-content">
        {/* GIS Summary Cards */}
        <div className="analytics-cards">
          <div className="analytics-card">
            <MapPin size={24} className="card-icon" />
            <div className="card-content">
              <div className="card-value">{totals.count.toLocaleString()}</div>
              <div className="card-label">{dataType === 'wells' ? 'Total Wells' : 'Total Tracts'}</div>
            </div>
          </div>

          {totals.acreage > 0 && (
            <div className="analytics-card">
              <TrendingUp size={24} className="card-icon" />
              <div className="card-content">
                <div className="card-value">{Math.round(totals.acreage).toLocaleString()}</div>
                <div className="card-label">Total Acres</div>
              </div>
            </div>
          )}

          {totals.royaltyAcres > 0 && (
            <div className="analytics-card">
              <BarChart3 size={24} className="card-icon" />
              <div className="card-content">
                <div className="card-value">{Math.round(totals.royaltyAcres).toLocaleString()}</div>
                <div className="card-label">Net Mineral Acres</div>
              </div>
            </div>
          )}

          {totals.nri > 0 && (
            <div className="analytics-card">
              <DollarSign size={24} className="card-icon" />
              <div className="card-content">
                <div className="card-value">{totals.nri.toFixed(4)}</div>
                <div className="card-label">Total NRI</div>
              </div>
            </div>
          )}

          {totals.orri > 0 && (
            <div className="analytics-card">
              <Activity size={24} className="card-icon" />
              <div className="card-content">
                <div className="card-value">{(totals.orri * 100).toFixed(2)}%</div>
                <div className="card-label">Total ORRI</div>
              </div>
            </div>
          )}
        </div>

        {/* GIS Tables */}
        <div className="analytics-tables">
          {countyData.length > 0 && (
            <div className="analytics-table-container">
              <h4><MapPin size={16} /> By County</h4>
              <table className="analytics-table clickable-table">
                <thead>
                  <tr>
                    <th>County</th>
                    <th>{dataType === 'wells' ? 'Wells' : 'Tracts'}</th>
                    <th>%</th>
                  </tr>
                </thead>
                <tbody>
                  {countyData
                    .sort((a, b) => b.count - a.count)
                    .map((item, idx) => (
                      <tr
                        key={idx}
                        className="clickable-row"
                        onClick={() => handleRowClick('county', item.name)}
                        title={`Click to filter by ${item.name}`}
                      >
                        <td>{item.name}</td>
                        <td className="count-cell">{item.count}</td>
                        <td className="percent-cell">
                          {((item.count / totals.count) * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {operatorData.length > 0 && (
            <div className="analytics-table-container">
              <h4><Users size={16} /> By Operator</h4>
              <table className="analytics-table clickable-table">
                <thead>
                  <tr>
                    <th>Operator</th>
                    <th>{dataType === 'wells' ? 'Wells' : 'Tracts'}</th>
                    <th>%</th>
                  </tr>
                </thead>
                <tbody>
                  {operatorData
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 8)
                    .map((item, idx) => (
                      <tr
                        key={idx}
                        className="clickable-row"
                        onClick={() => handleRowClick('operator', item.name)}
                        title={`Click to filter by ${item.name}`}
                      >
                        <td>{item.name}</td>
                        <td className="count-cell">{item.count}</td>
                        <td className="percent-cell">
                          {((item.count / totals.count) * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {statusData.length > 0 && (
            <div className="analytics-table-container">
              <h4><BarChart3 size={16} /> By {dataType === 'wells' ? 'Well Status' : 'Lease Status'}</h4>
              <table className="analytics-table clickable-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>{dataType === 'wells' ? 'Wells' : 'Tracts'}</th>
                    <th>%</th>
                  </tr>
                </thead>
                <tbody>
                  {statusData
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 8)
                    .map((item, idx) => (
                      <tr
                        key={idx}
                        className="clickable-row"
                        onClick={() => handleRowClick('status', item.name)}
                        title={`Click to filter by ${item.name}`}
                      >
                        <td>{item.name}</td>
                        <td className="count-cell">{item.count}</td>
                        <td className="percent-cell">
                          {((item.count / totals.count) * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {formationData.length > 0 && (
            <div className="analytics-table-container">
              <h4><Activity size={16} /> By Formation</h4>
              <table className="analytics-table clickable-table">
                <thead>
                  <tr>
                    <th>Formation</th>
                    <th>Wells</th>
                    <th>%</th>
                  </tr>
                </thead>
                <tbody>
                  {formationData
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 8)
                    .map((item, idx) => (
                      <tr
                        key={idx}
                        className="clickable-row"
                        onClick={() => handleRowClick('formation', item.name)}
                        title={`Click to filter by ${item.name}`}
                      >
                        <td>{item.name}</td>
                        <td className="count-cell">{item.count}</td>
                        <td className="percent-cell">
                          {((item.count / totals.count) * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ============================================ */}
        {/* PRODUCTION REVENUE ANALYTICS (Gold Section) */}
        {/* ============================================ */}
        {revenueData && revenueData.totals && (
          <>
            <div className="analytics-section-header revenue-header">
              <DollarSign size={20} />
              <h4>Production Revenue Analytics</h4>
              {currentMoM && <ChangeIndicator change={currentMoM} />}
            </div>

            <div className="analytics-cards">
              <div className="analytics-card revenue-card">
                <DollarSign size={24} className="card-icon" />
                <div className="card-content">
                  <div className="card-value">
                    ${formatNumber(revenueData.totals.total_revenue)}
                  </div>
                  <div className="card-label">Total Revenue</div>
                </div>
              </div>

              <div className="analytics-card revenue-card">
                <BarChart3 size={24} className="card-icon" />
                <div className="card-content">
                  <div className="card-value">
                    {formatNumber(revenueData.totals.total_records)}
                  </div>
                  <div className="card-label">Revenue Records</div>
                </div>
              </div>

              <div className="analytics-card revenue-card">
                <Activity size={24} className="card-icon" />
                <div className="card-content">
                  <div className="card-value">
                    {revenueData.totals.unique_dsus?.toLocaleString() || 0}
                  </div>
                  <div className="card-label">Unique DSUs</div>
                </div>
              </div>
            </div>

            {/* Revenue Tables */}
            <div className="analytics-tables">
              {revenueData.byProduct && revenueData.byProduct.length > 0 && (
                <div className="analytics-table-container">
                  <h4><Droplets size={16} /> Revenue by Product</h4>
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Revenue</th>
                        <th>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenueData.byProduct.map((item, idx) => (
                        <tr key={idx}>
                          <td style={{ textTransform: 'capitalize' }}>{item.product}</td>
                          <td className="revenue-cell">
                            ${formatNumber(item.revenue)}
                          </td>
                          <td className="percent-cell">
                            {((item.revenue / revenueData.totals.total_revenue) * 100).toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {revenueData.byCounty && revenueData.byCounty.length > 0 && (
                <div className="analytics-table-container">
                  <h4><MapPin size={16} /> Revenue by County</h4>
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>County</th>
                        <th>Revenue</th>
                        <th>Records</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenueData.byCounty.slice(0, 6).map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.county}</td>
                          <td className="revenue-cell">
                            ${formatNumber(item.revenue)}
                          </td>
                          <td className="count-cell">
                            {formatNumber(item.records, 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* ============================================ */}
        {/* REVENUE TREND (12-Month Sparkline) */}
        {/* ============================================ */}
        {trendData && trendData.trend && trendData.trend.length > 0 && (
          <>
            <div className="analytics-section-header trend-header">
              <TrendingUp size={20} />
              <h4>Revenue Trend (12 Months)</h4>
            </div>

            <div className="trend-section">
              <div className="trend-chart-container">
                <Sparkline
                  data={sparklineData}
                  width={200}
                  height={50}
                  color="#10b981"
                />
                <div className="trend-labels">
                  <span>{trendData.trend[0]?.month}</span>
                  <span>{trendData.trend[trendData.trend.length - 1]?.month}</span>
                </div>
              </div>

              <div className="trend-stats">
                {trendData.trend.slice(-3).reverse().map((month, idx) => (
                  <div key={idx} className="trend-month">
                    <span className="month-label">{month.month}</span>
                    <span className="month-value">${formatNumber(month.revenue)}</span>
                    <ChangeIndicator change={month.change} size="small" />
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ============================================ */}
        {/* TOP DSUs LEADERBOARD */}
        {/* ============================================ */}
        {topDsus && topDsus.topDsus && topDsus.topDsus.length > 0 && (
          <>
            <div className="analytics-section-header leaderboard-header">
              <Award size={20} />
              <h4>Top Performing DSUs</h4>
            </div>

            <div className="analytics-table-container leaderboard">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>DSU Name</th>
                    <th>Total Revenue</th>
                    <th>Last Production</th>
                  </tr>
                </thead>
                <tbody>
                  {topDsus.topDsus.slice(0, 5).map((dsu, idx) => (
                    <tr key={idx} className={idx === 0 ? 'top-performer' : ''}>
                      <td className="rank-cell">
                        {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                      </td>
                      <td className="dsu-name">{dsu.dsu_name}</td>
                      <td className="revenue-cell highlight">
                        ${formatNumber(dsu.total_revenue)}
                      </td>
                      <td className="date-cell">
                        {dsu.last_production?.substring(0, 7) || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ============================================ */}
        {/* PRODUCTION FORECAST */}
        {/* ============================================ */}
        {forecastData && forecastData.totals && (
          <>
            <div className="analytics-section-header forecast-header">
              <Zap size={20} />
              <h4>Production Forecast (2026-2027)</h4>
            </div>

            <div className="analytics-cards forecast-cards">
              <div className="analytics-card forecast-card">
                <Droplets size={24} className="card-icon oil" />
                <div className="card-content">
                  <div className="card-value">
                    {formatNumber(forecastData.totals.total_oil)} BBL
                  </div>
                  <div className="card-label">Forecasted Oil</div>
                </div>
              </div>

              <div className="analytics-card forecast-card">
                <Activity size={24} className="card-icon gas" />
                <div className="card-content">
                  <div className="card-value">
                    {formatNumber(forecastData.totals.total_gas)} BOE
                  </div>
                  <div className="card-label">Forecasted Gas</div>
                </div>
              </div>

              <div className="analytics-card forecast-card">
                <Target size={24} className="card-icon ngl" />
                <div className="card-content">
                  <div className="card-value">
                    {formatNumber(forecastData.totals.total_ngl)} BOE
                  </div>
                  <div className="card-label">Forecasted NGL</div>
                </div>
              </div>

              <div className="analytics-card forecast-card">
                <Calendar size={24} className="card-icon wells" />
                <div className="card-content">
                  <div className="card-value">
                    {forecastData.totals.total_wells?.toLocaleString()}
                  </div>
                  <div className="card-label">Wells Forecasted</div>
                </div>
              </div>
            </div>

            {/* Forecast by Month */}
            {forecastData.forecast && forecastData.forecast.length > 0 && (
              <div className="analytics-table-container forecast-table">
                <h4><Calendar size={16} /> Monthly Forecast</h4>
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Oil (BBL)</th>
                      <th>Gas (BOE)</th>
                      <th>NGL (BOE)</th>
                      <th>Wells</th>
                    </tr>
                  </thead>
                  <tbody>
                    {forecastData.forecast.slice(0, 6).map((month, idx) => (
                      <tr key={idx}>
                        <td>{month.month}</td>
                        <td className="count-cell">{formatNumber(month.oil_bbl, 0)}</td>
                        <td className="count-cell">{formatNumber(month.gas_boe, 0)}</td>
                        <td className="count-cell">{formatNumber(month.ngl_boe, 0)}</td>
                        <td className="count-cell">{month.wells?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ============================================ */}
        {/* OWNERSHIP BREAKDOWN */}
        {/* ============================================ */}
        {ownershipData && ownershipData.breakdown && ownershipData.breakdown.length > 0 && (
          <>
            <div className="analytics-section-header ownership-header">
              <FileText size={20} />
              <h4>Ownership Interest Breakdown</h4>
            </div>

            <div className="analytics-table-container ownership-table">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Interest Type</th>
                    <th>Count</th>
                    <th>Total NRI</th>
                    <th>Total ORRI</th>
                    <th>Tracts</th>
                  </tr>
                </thead>
                <tbody>
                  {ownershipData.breakdown.slice(0, 6).map((item, idx) => (
                    <tr key={idx}>
                      <td className="interest-type">{item.interest_type}</td>
                      <td className="count-cell">{item.count?.toLocaleString()}</td>
                      <td className="count-cell">{item.total_nri?.toFixed(4) || '0'}</td>
                      <td className="count-cell">{item.total_orri?.toFixed(4) || '0'}</td>
                      <td className="count-cell">{item.unique_tracts?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {loading && (
          <div className="analytics-loading">
            <Activity size={20} className="spinning" />
            <span>Loading enhanced analytics...</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default AnalyticsPanel;
